import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { cva, type VariantProps } from 'class-variance-authority';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { cn } from '../lib/utils';

import type { ChangeEvent, Ref, TextareaHTMLAttributes } from 'react';

export type TextareaProps = {
  disableAutoResizing?: boolean;
  focus?: boolean;
  maxRows?: number;
  readOnly?: boolean;
  ref?: Ref<HTMLTextAreaElement>;
  selectOnFocus?: boolean;
  value?: string;
  onCursorChange?: (position: number) => void;
} & TextareaHTMLAttributes<HTMLTextAreaElement> &
  VariantProps<typeof textareaVariants>;

const textareaVariants = cva(
  'flex w-full resize-none p-2 min-h-[2.26rem] rounded-md border border-input bg-background ring-offset-background text-sm placeholder:text-muted-foreground focus-visible:border-ring focus-visible:outline-none focus-visible:ring-0.5 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        error: 'border-destructive focus-visible:ring-destructive',
        borderless: 'border-none bg-transparent focus-visible:ring-0',
      },
      inputSize: {
        default: 'text-sm',
        md: 'text-md min-h-[2.65rem]',
        lg: 'text-xl min-h-[3rem]',
        xl: 'text-4xl md:text-5xl min-h-[4rem]',
      },
    },
    defaultVariants: {
      inputSize: 'default',
    },
  },
);

const rowHeight = { default: 24, md: 24, lg: 30, xl: 52 };

const Textarea = ({
  className,
  disableAutoResizing,
  focus,
  inputSize,
  maxRows,
  onChange,
  onCursorChange,
  readOnly,
  ref: forwardedRef,
  selectOnFocus,
  rows = 2,
  value,
  variant,
  ...props
}: TextareaProps) => {
  const [internalValue, setInternalValue] = useState('');
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const composedRef = useComposedRefs(forwardedRef, textAreaRef);

  const size = inputSize ?? 'default';
  const minHeight = rows * rowHeight[size];
  const maxHeight = maxRows && maxRows >= rows ? maxRows * rowHeight[size] + 16 : 1024;

  // Sync value before paint so the resize effect below measures the correct content
  // on mount. (Also positions the cursor at the end of pre-filled content, since the
  // browser places the caret at the end after a value property update.)
  useLayoutEffect(() => {
    setInternalValue(value ?? '');
  }, [value]);

  useLayoutEffect(() => {
    if (!textAreaRef.current || disableAutoResizing) {
      return;
    }
    textAreaRef.current.style.height = 'auto';
    const next = Math.min(maxHeight, Math.max(minHeight, textAreaRef.current.scrollHeight));
    textAreaRef.current.style.height = `${next + 2}px`;
  }, [internalValue, disableAutoResizing, minHeight, maxHeight]);

  useEffect(() => {
    if (!focus) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      if (textAreaRef.current) {
        textAreaRef.current.focus();
        if (selectOnFocus) {
          textAreaRef.current.select();
        }
      }
    }, 50);
    return () => window.clearTimeout(timeoutId);
  }, [focus, selectOnFocus]);

  const handleChange = (evt: ChangeEvent<HTMLTextAreaElement>) => {
    setInternalValue(evt.target.value);
    onChange?.(evt);
  };

  const handleSelect = () => {
    if (onCursorChange) {
      onCursorChange(textAreaRef.current?.selectionStart ?? 0);
    }
  };

  return (
    <textarea
      {...props}
      className={cn(textareaVariants({ variant, className, inputSize }))}
      onChange={handleChange}
      onSelect={handleSelect}
      readOnly={readOnly}
      ref={composedRef}
      rows={rows}
      value={internalValue}
    />
  );
};

Textarea.displayName = 'Textarea';

export { Textarea };
